import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { checkPolarCheckout } from './lib/subscription';
import { supabase } from './lib/supabase';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get('checkout_id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (checkoutId) {
      verifyPayment(checkoutId);
    } else {
      setError('Missing checkout_id');
      setLoading(false);
    }
  }, [checkoutId]);

  const verifyPayment = async (cid: string) => {
    try {
      // 1. Check payment status in Polar
      const checkout = await checkPolarCheckout(cid);

      if (checkout) {
        setCheckoutStatus(checkout.status);

        // 2. If payment is successful, the webhook has already handled the upgrade
        if (checkout.status === 'succeeded') {
          // No need to manually upgrade - webhook already handled it correctly
          console.log('Payment successful - user upgraded by webhook');
        }
      } else {
        setError('Failed to verify payment');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while verifying payment');
    } finally {
      setLoading(false);
    }
  };

  const goHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
        {loading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Ověřuji platbu...</p>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="rounded-full bg-red-100 p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Chyba</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={goHome}
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Zpět do aplikace
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="rounded-full bg-green-100 p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Platba úspěšná!</h2>
            <p className="text-gray-600 mb-6">
              Vaše Premium předplatné bylo úspěšně aktivováno. Nyní máte přístup ke všem Premium funkcím včetně zvýšeného limitu úkolů, pokročilých AI návrhů a možnosti sdílení sticky notes.
            </p>
            
            <div className="mb-6 bg-indigo-50 p-4 rounded-lg text-sm">
              <div className="text-indigo-800">
                <p className="font-medium mb-2">✨ Premium aktivováno!</p>
                <ul className="text-left space-y-1">
                  <li>• Až 1000 úkolů</li>
                  <li>• 100 AI návrhů denně</li>
                  <li>• Sdílení sticky notes</li>
                  <li>• Prioritní podpora</li>
                </ul>
              </div>
            </div>
            
            <button
              onClick={goHome}
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Zpět do aplikace
            </button>
          </div>
        )}
      </div>
    </div>
  );
}